import {all, call, getContext, put, takeEvery} from 'redux-saga/effects';
import type {Action} from '../utils';
import i18n from '../../../i18n';
import type {IPerson} from '../../api/domain/channelsuite/user-services/customer-master-data';
import type {IEmployeeBranch} from '../../api/domain/channelsuite/user-services/employee-master-data';
import type {CustomerMasterDataApiService, EmployeeMasterDataApiService} from '../../api/services';
import {NotificationType} from '../../enum';
import {addNotification} from '../application/application-actions';
import {logError} from '../logging-api/logging-api-actions';
import {
    fetchCustomerBranch,
    fetchCustomerBranchFailure,
    fetchCustomerBranchSuccess,
    fetchUserPerson,
    fetchUserPersonFailure,
    fetchUserPersonSuccess,
} from './customer-master-data-api-actions';
import de from './translations/de.json';
import en from './translations/en.json';
import fr from './translations/fr.json';
import it from './translations/it.json';

function* addTranslations() {
    i18n.addResources('de', de);
    i18n.addResources('en', en);
    i18n.addResources('fr', fr);
    i18n.addResources('it', it);

    yield;
}

function* handleFetchPerson() {
    const api: CustomerMasterDataApiService = yield getContext('customerMasterDataApiService');

    try {
        const persons: IPerson[] = yield call([api, api.fetchOwnerOnlyPerson]);
        yield put(fetchUserPersonSuccess(persons[0]));
        if (persons[0].branchExternalId) {
            yield put(fetchCustomerBranch(persons[0].branchExternalId));
        }
    } catch (error: unknown) {
        yield put(logError(`could not fetch user person: ${error}`));
        yield put(fetchUserPersonFailure());
    }
}

function* handleFetchCustomerBranch(action: Action<string>) {
    const api: EmployeeMasterDataApiService = yield getContext('employeeMasterDataApiService');

    try {
        const branch: IEmployeeBranch = yield call([api, api.getBranchByExternalId], action.payload);
        yield put(fetchCustomerBranchSuccess(branch));
    } catch (error: unknown) {
        yield put(logError(`could not fetch customer branch: ${error}`));
        yield put(fetchCustomerBranchFailure());
    }
}

function* handleFetchPersonFailure() {
    yield put(
        addNotification({
            title: i18n.t('customer-master-data-api.error.fetch-person'),
            type: NotificationType.ERROR,
        }),
    );
}

export function* customerMasterDataApiSaga() {
    yield all([
        call(addTranslations),
        takeEvery(fetchUserPerson, handleFetchPerson),
        takeEvery(fetchUserPersonFailure, handleFetchPersonFailure),
        takeEvery(fetchCustomerBranch, handleFetchCustomerBranch),
    ]);
}
